/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {APP_ID, Inject, Injectable, NgZone, RenderComponentType, Renderer, RootRenderer, ViewEncapsulation} from '@angular/core';
import {AnimationDriver, DOCUMENT} from '@angular/platform-browser';

import {isBlank, isPresent, stringify} from './facade/lang';
import {AnimationKeyframe, AnimationPlayer, AnimationStyles, RenderDebugInfo} from './private_import_core';
import {NAMESPACE_URIS, SharedStylesHost, flattenStyles, getDOM, isNamespaced, shimContentAttribute, shimHostAttribute, splitNamespace} from './private_import_platform-browser';

const /** @type {?} */ TEMPLATE_COMMENT_TEXT = 'template bindings={}';
const /** @type {?} */ TEMPLATE_BINDINGS_EXP = /^template bindings=(.*)$/;


export class ServerRootRenderer {
  protected registeredComponents: Map<string, ServerRenderer> = new Map<string, ServerRenderer>();
/**
 * @param {?} document
 * @param {?} sharedStylesHost
 * @param {?} animationDriver
 * @param {?} appId
 * @param {?} _zone
 */
constructor(
public document: any,
public sharedStylesHost: SharedStylesHost,
public animationDriver: AnimationDriver,
public appId: string,
private _zone: NgZone) {}
/**
 * @param {?} componentProto
 * @return {?}
 */
renderComponent(componentProto: RenderComponentType): Renderer {
    let /** @type {?} */ renderer = this.registeredComponents.get(componentProto.id);
    if (!renderer) {
      renderer = new ServerRenderer(
          this, componentProto, this.animationDriver, `${this.appId}-${componentProto.id}`,
          this._zone);
      this.registeredComponents.set(componentProto.id, renderer);
    }
    return renderer;
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Inject, args: [DOCUMENT, ] }, ]},
{type: SharedStylesHost, },
{type: AnimationDriver, },
{type: undefined, decorators: [{ type: Inject, args: [APP_ID, ] }, ]},
{type: NgZone, },
];
}

function ServerRootRenderer_tsickle_Closure_declarations() {
 /** @type {?} */
ServerRootRenderer.decorators;
 /**
  * @nocollapse
  * @type {?}
  */
ServerRootRenderer.ctorParameters;
 /** @type {?} */
ServerRootRenderer.prototype.registeredComponents;
 /** @type {?} */
ServerRootRenderer.prototype.document;
 /** @type {?} */
ServerRootRenderer.prototype.sharedStylesHost;
 /** @type {?} */
ServerRootRenderer.prototype.animationDriver;
 /** @type {?} */
ServerRootRenderer.prototype.appId;
 /** @type {?} */
ServerRootRenderer.prototype._zone;
}


export class ServerRenderer implements Renderer {
private _contentAttr: string;
private _hostAttr: string;
private _styles: string[];
/**
 * @param {?} _rootRenderer
 * @param {?} componentProto
 * @param {?} _animationDriver
 * @param {?} styleShimId
 * @param {?} _zone
 */
constructor(
private _rootRenderer: ServerRootRenderer,
private componentProto: RenderComponentType,
private _animationDriver: AnimationDriver, styleShimId: string,
private _zone: NgZone) {
    this._styles = flattenStyles(styleShimId, componentProto.styles, []);
    if (componentProto.encapsulation === ViewEncapsulation.Native) {
      throw new Error('Native encapsulation is not supported on the server!');
    }
    if (this.componentProto.encapsulation === ViewEncapsulation.Emulated) {
      this._contentAttr = shimContentAttribute(styleShimId);
      this._hostAttr = shimHostAttribute(styleShimId);
    } else {
      this._contentAttr = null;
      this._hostAttr = null;
    }
  }
/**
 * @param {?} selectorOrNode
 * @param {?} debugInfo
 * @return {?}
 */
selectRootElement(selectorOrNode: string|any, debugInfo: RenderDebugInfo): Element {
    let /** @type {?} */ el: any /** TODO #9100 */;
    if (typeof selectorOrNode === 'string') {
      el = getDOM().querySelector(this._rootRenderer.document, selectorOrNode);
      if (isBlank(el)) {
        throw new Error(`The selector "${selectorOrNode}" did not match any elements`);
      }
    } else {
      el = selectorOrNode;
    }
    getDOM().clearNodes(el);
    return el;
  }
/**
 * @param {?} parent
 * @param {?} name
 * @param {?} debugInfo
 * @return {?}
 */
createElement(parent: Element, name: string, debugInfo: RenderDebugInfo): Node {
    let /** @type {?} */ el: any;
    if (isNamespaced(name)) {
      const /** @type {?} */ nsAndName = splitNamespace(name);
      el = getDOM().createElementNS(NAMESPACE_URIS[nsAndName[0]], nsAndName[1]);
    } else {
      el = getDOM().createElement(name);
    }
    if (isPresent(this._contentAttr)) {
      getDOM().setAttribute(el, this._contentAttr, '');
    }
    if (isPresent(parent)) {
      getDOM().appendChild(parent, el);
    }
    return el;
  }
/**
 * @param {?} hostElement
 * @return {?}
 */
createViewRoot(hostElement: any): any {
    let /** @type {?} */ nodesParent: any /** TODO #9100 */;
    if (isPresent(this._hostAttr)) {
      getDOM().setAttribute(hostElement, this._hostAttr, '');
    }
    nodesParent = hostElement;
    return nodesParent;
  }
/**
 * @param {?} parentElement
 * @param {?} debugInfo
 * @return {?}
 */
createTemplateAnchor(parentElement: any, debugInfo: RenderDebugInfo): any {
    const /** @type {?} */ comment = getDOM().createComment(TEMPLATE_COMMENT_TEXT);
    if (isPresent(parentElement)) {
      getDOM().appendChild(parentElement, comment);
    }
    return comment;
  }
/**
 * @param {?} parentElement
 * @param {?} value
 * @param {?} debugInfo
 * @return {?}
 */
createText(parentElement: any, value: string, debugInfo: RenderDebugInfo): any {
    const /** @type {?} */ node = getDOM().createTextNode(value);
    if (isPresent(parentElement)) {
      getDOM().appendChild(parentElement, node);
    }
    return node;
  }
/**
 * @param {?} parentElement
 * @param {?} nodes
 * @return {?}
 */
projectNodes(parentElement: any, nodes: any[]) {
    if (isBlank(parentElement)) return;
    appendNodes(parentElement, nodes);
  }
/**
 * @param {?} node
 * @param {?} viewRootNodes
 * @return {?}
 */
attachViewAfter(node: any, viewRootNodes: any[]) { moveNodesAfterSibling(node, viewRootNodes); }
/**
 * @param {?} viewRootNodes
 * @return {?}
 */
detachView(viewRootNodes: any[]) {
    for (let /** @type {?} */ i = 0; i < viewRootNodes.length; i++) {
      getDOM().remove(viewRootNodes[i]);
    }
  }
/**
 * @param {?} hostElement
 * @param {?} viewAllNodes
 * @return {?}
 */
destroyView(hostElement: any, viewAllNodes: any[]) {}
/**
 * @param {?} renderElement
 * @param {?} name
 * @param {?} callback
 * @return {?}
 */
listen(renderElement: any, name: string, callback: Function): Function {
    // Note: We are not using the EventsPlugin here as this is not needed
    // to run our tests.
    const /** @type {?} */ outsideHandler = (event: any) => this._zone.runGuarded(() => callback(event));
    return this._zone.runOutsideAngular(
        () => getDOM().onAndCancel(renderElement, name, outsideHandler));
  }
/**
 * @param {?} target
 * @param {?} name
 * @param {?} callback
 * @return {?}
 */
listenGlobal(target: string, name: string, callback: Function): Function {
    const /** @type {?} */ renderElement = getDOM().getGlobalEventTarget(target);
    return this.listen(renderElement, name, callback);
  }
/**
 * @param {?} renderElement
 * @param {?} propertyName
 * @param {?} propertyValue
 * @return {?}
 */
setElementProperty(renderElement: any, propertyName: string, propertyValue: any): void {
    getDOM().setProperty(renderElement, propertyName, propertyValue);
  }
/**
 * @param {?} renderElement
 * @param {?} attributeName
 * @param {?} attributeValue
 * @return {?}
 */
setElementAttribute(renderElement: any, attributeName: string, attributeValue: string): void {
    let /** @type {?} */ attrNs: string;
    let /** @type {?} */ attrNameWithoutNs = attributeName;
    if (isNamespaced(attributeName)) {
      const /** @type {?} */ nsAndName = splitNamespace(attributeName);
      attrNameWithoutNs = nsAndName[1];
      attributeName = nsAndName[0] + ':' + nsAndName[1];
      attrNs = NAMESPACE_URIS[nsAndName[0]];
    }
    if (isPresent(attributeValue)) {
      if (isPresent(attrNs)) {
        getDOM().setAttributeNS(renderElement, attrNs, attributeName, attributeValue);
      } else {
        getDOM().setAttribute(renderElement, attributeName, attributeValue);
      }
    } else {
      if (isPresent(attrNs)) {
        getDOM().removeAttributeNS(renderElement, attrNs, attrNameWithoutNs);
      } else {
        getDOM().removeAttribute(renderElement, attributeName);
      }
    }
  }
/**
 * @param {?} renderElement
 * @param {?} propertyName
 * @param {?} propertyValue
 * @return {?}
 */
setBindingDebugInfo(renderElement: any, propertyName: string, propertyValue: string): void {
    if (getDOM().isCommentNode(renderElement)) {
      const /** @type {?} */ existingBindings =
          getDOM().getText(renderElement).replace(/\n/g, '').match(TEMPLATE_BINDINGS_EXP);
      const /** @type {?} */ parsedBindings = JSON.parse(existingBindings[1]);
      ( /** @type {?} */((parsedBindings as any)) /** TODO #9100 */)[propertyName] = propertyValue;
      getDOM().setText(
          renderElement,
          TEMPLATE_COMMENT_TEXT.replace('{}', JSON.stringify(parsedBindings, null, 2)));
    } else {
      this.setElementAttribute(renderElement, propertyName, propertyValue);
    }
  }
/**
 * @param {?} renderElement
 * @param {?} className
 * @param {?} isAdd
 * @return {?}
 */
setElementClass(renderElement: any, className: string, isAdd: boolean): void {
    if (isAdd) {
      getDOM().addClass(renderElement, className);
    } else {
      getDOM().removeClass(renderElement, className);
    }
  }
/**
 * @param {?} renderElement
 * @param {?} styleName
 * @param {?} styleValue
 * @return {?}
 */
setElementStyle(renderElement: any, styleName: string, styleValue: string): void {
    if (isPresent(styleValue)) {
      getDOM().setStyle(renderElement, styleName, stringify(styleValue));
    } else {
      getDOM().removeStyle(renderElement, styleName);
    }
  }
/**
 * @param {?} renderElement
 * @param {?} methodName
 * @param {?} args
 * @return {?}
 */
invokeElementMethod(renderElement: any, methodName: string, args: any[]): void {
    getDOM().invoke(renderElement, methodName, args);
  }
/**
 * @param {?} renderNode
 * @param {?} text
 * @return {?}
 */
setText(renderNode: any, text: string): void { getDOM().setText(renderNode, text); }
/**
 * @param {?} element
 * @param {?} startingStyles
 * @param {?} keyframes
 * @param {?} duration
 * @param {?} delay
 * @param {?} easing
 * @param {?=} previousPlayers
 * @return {?}
 */
animate(
      element: any, startingStyles: AnimationStyles, keyframes: AnimationKeyframe[],
      duration: number, delay: number, easing: string,
      previousPlayers: AnimationPlayer[] = []): AnimationPlayer {
    return this._animationDriver.animate(
        element, startingStyles, keyframes, duration, delay, easing, previousPlayers);
  }
}

function ServerRenderer_tsickle_Closure_declarations() {
 /** @type {?} */
ServerRenderer.prototype._contentAttr;
 /** @type {?} */
ServerRenderer.prototype._hostAttr;
 /** @type {?} */
ServerRenderer.prototype._styles;
 /** @type {?} */
ServerRenderer.prototype._rootRenderer;
 /** @type {?} */
ServerRenderer.prototype.componentProto;
 /** @type {?} */
ServerRenderer.prototype._animationDriver;
 /** @type {?} */
ServerRenderer.prototype._zone;
}

/**
 * @param {?} sibling
 * @param {?} nodes
 * @return {?}
 */
function moveNodesAfterSibling(sibling: any /** TODO #9100 */, nodes: any /** TODO #9100 */) {
  const /** @type {?} */ parent = getDOM().parentElement(sibling);
  if (nodes.length > 0 && isPresent(parent)) {
    const /** @type {?} */ nextSibling = getDOM().nextSibling(sibling);
    if (isPresent(nextSibling)) {
      for (let /** @type {?} */ i = 0; i < nodes.length; i++) {
        getDOM().insertBefore(nextSibling, nodes[i]);
      }
    } else {
      for (let /** @type {?} */ i = 0; i < nodes.length; i++) {
        getDOM().appendChild(parent, nodes[i]);
      }
    }
  }
}
/**
 * @param {?} parent
 * @param {?} nodes
 * @return {?}
 */
function appendNodes(parent: any /** TODO #9100 */, nodes: any /** TODO #9100 */) {
  for (let /** @type {?} */ i = 0; i < nodes.length; i++) {
    getDOM().appendChild(parent, nodes[i]);
  }
}
/**
 * @param {?} eventHandler
 * @return {?}
 */
function decoratePreventDefault(eventHandler: Function): Function {
  return (event: any /** TODO #9100 */) => {
    const /** @type {?} */ allowDefaultBehavior = eventHandler(event);
    if (allowDefaultBehavior === false) {
      // TODO(tbosch): move preventDefault into event plugins...
      getDOM().preventDefault(event);
    }
  };
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
